import { useState, useRef, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { posthog } from '../lib/analytics/posthogClient';
import { useEvent } from '../hooks';
import { categoryColors, categoryLabels, formatDateRange } from '../utils';
import { hasRealImage } from '../utils/imageUtils';
import { imageLoadingProps } from '../utils/imagePriority';
import { trackEvent } from '../utils/analytics';
import { removeSsrShell, exposeSsrRoot, clearDisputedDateHead } from '../utils/ssrShell';
import { PageHeader } from '../components/layout/PageHeader';
import { EventMeta } from '../components/events/EventMeta';
import { EventActions } from '../components/events/EventActions';
import { DateUnderReviewNotice } from '../components/events/DateUnderReviewNotice';
import { Chip } from '../components/ui/Chip';
import { BuzzPin } from '../components/icons/BuzzPin';
// TODO: Re-enable when sponsor/stands data comes from DB
// import { SponsorBanner, type Sponsor } from '../components/sponsors';
// import { StandList, type Stand } from '../components/stands';
import styles from './EventPage.module.css';

interface ImageGalleryProps {
  images: string[];
}

/**
 * Horizontal carousel with CSS scroll-snap. Touch swipe on mobile, arrows on desktop.
 *
 * T103-FE: l'ingombro non dipende dal caricamento delle foto. Lo slot è sempre 4:3 alla larghezza
 * disponibile (foto contenute, mai ritagliate), la riga dei puntini è riservata in base alle foto
 * presenti all'ingresso, e se tutte falliscono resta uno slot con segnaposto invece di sparire.
 */
function ImageGallery({ images: rawImages }: ImageGalleryProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [broken, setBroken] = useState<Set<string>>(() => new Set());
  const images = rawImages.filter((src) => !broken.has(src));
  // Riga dei puntini riservata dall'elenco all'ingresso: una foto che fallisce non la toglie.
  const reserveDots = rawImages.length > 1;
  // Dopo un errore le slide diminuiscono: l'indice attivo non può uscire dall'elenco.
  const current = Math.min(activeIndex, Math.max(images.length - 1, 0));

  const scrollToIndex = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const slide = track.children[index] as HTMLElement | undefined;
    if (slide) {
      slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }
  }, []);

  const handleScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const scrollLeft = track.scrollLeft;
    const slideWidth = track.offsetWidth;
    const newIndex = Math.round(scrollLeft / slideWidth);
    setActiveIndex(Math.max(0, Math.min(newIndex, images.length - 1)));
  }, [images.length]);

  const goPrev = useCallback(() => {
    const newIdx = Math.max(0, current - 1);
    setActiveIndex(newIdx);
    scrollToIndex(newIdx);
  }, [current, scrollToIndex]);

  const goNext = useCallback(() => {
    const newIdx = Math.min(images.length - 1, current + 1);
    setActiveIndex(newIdx);
    scrollToIndex(newIdx);
  }, [current, images.length, scrollToIndex]);

  return (
    <div className={styles.gallery}>
      <div ref={trackRef} className={styles.galleryTrack} onScroll={handleScroll}>
        {images.length === 0 ? (
          <div className={styles.gallerySlide}>
            <div className={styles.galleryPlaceholder}>Foto non disponibile</div>
          </div>
        ) : (
          images.map((src, idx) => (
            <div key={src} className={styles.gallerySlide}>
              <img
                src={src}
                alt={`Foto ${idx + 1}`}
                className={styles.galleryImage}
                // T105-FE: la prima foto è il candidato LCP del dettaglio (eager + fetchpriority
                // high); le altre slide stanno fuori schermo e restano lazy.
                {...imageLoadingProps(idx === 0)}
                onError={() => {
                  setBroken((prev) => {
                    if (prev.has(src)) return prev;
                    const next = new Set(prev);
                    next.add(src);
                    return next;
                  });
                }}
              />
            </div>
          ))
        )}
      </div>

      {images.length > 1 && (
        <>
          <button
            type="button"
            className={`${styles.galleryNav} ${styles.galleryNavPrev}`}
            onClick={goPrev}
            aria-label="Immagine precedente"
            disabled={current === 0}
          >
            &#8249;
          </button>
          <button
            type="button"
            className={`${styles.galleryNav} ${styles.galleryNavNext}`}
            onClick={goNext}
            aria-label="Immagine successiva"
            disabled={current === images.length - 1}
          >
            &#8250;
          </button>
        </>
      )}

      {reserveDots && (
        <div className={styles.galleryDots}>
          {images.length > 1 &&
            images.map((_, idx) => (
              <button
                key={idx}
                type="button"
                className={`${styles.galleryDot} ${idx === current ? styles.galleryDotActive : ''}`}
                onClick={() => {
                  setActiveIndex(idx);
                  scrollToIndex(idx);
                }}
                aria-label={`Vai alla foto ${idx + 1}`}
              />
            ))}
        </div>
      )}
    </div>
  );
}

/**
 * EventPage - Full dedicated event detail page.
 * Accessible at /event/:id with shareable URL.
 */
export function EventPage() {
  const { id } = useParams<{ id: string }>();
  const { event, loading, error, dateUnderReview } = useEvent(id ?? '');

  // Shell rule (R15): the shell leaves only once the app shows the event —
  // settled, present and error-free (`!loading && event && !error`). While
  // the fetch is in flight or has failed, it is the page's only real
  // content. On desktop `EventModal`, not this component, draws
  // `/event/:id`. Full rule: `ssrShell.ts`.
  useEffect(() => {
    if (loading) return;
    if (error || !event) {
      // Evento assente o in errore: il guscio resta, ma l'app sotto resta
      // usabile (T102-INT). In revisione data decide l'effetto qui sotto.
      if (!dateUnderReview) exposeSsrRoot();
      return;
    }
    removeSsrShell();
  }, [loading, error, event, dateUnderReview]);

  // T208: when the background revalidation (useEvent) replaces an event the reader already saw with
  // the date-review notice or the not-found error, a live region inserted with its text is not
  // reliably announced, so focus moves to the new heading. The swap changes no box size (no CLS).
  // The first paint of an error or notice (nothing shown before) keeps the focus where it was.
  const mainRef = useRef<HTMLElement>(null);
  const eventWasShown = useRef(false);
  useEffect(() => {
    if (loading) return;
    if (event && !error && !dateUnderReview) {
      eventWasShown.current = true;
      return;
    }
    if (!eventWasShown.current) return;
    eventWasShown.current = false;
    // Focus on an element that stays mounted (header, back button) is the reader's: leave it. Focus that
    // was inside the unmounted event content has already fallen back to the body.
    const active = document.activeElement;
    if (active && active !== document.body) return;
    const heading = mainRef.current?.querySelector('h1');
    heading?.setAttribute('tabindex', '-1');
    heading?.focus();
  }, [loading, event, error, dateUnderReview]);

  // T010 (R7): the date-review answer is authoritative content — once its
  // notice has rendered (this effect runs after that commit), a server shell
  // still carrying the disputed date must go, or the DOM keeps advertising a
  // date the API just withholds. The same holds for the cached document's
  // `<head>` (title, description/og metas, Event JSON-LD) — the scrub is
  // event-document-guarded inside. `removeSsrShell` waits two frames, so the
  // removal lands after the notice paint. Loading, network errors, normal
  // 404 and valid content keep the rule above unchanged.
  useEffect(() => {
    if (!dateUnderReview) return;
    removeSsrShell();
    clearDisputedDateHead();
  }, [dateUnderReview]);

  // Track event page view when the event data loads
  useEffect(() => {
    if (event) {
      trackEvent('event_view', {
        event_id: event.id,
        category: event.category,
        city: event.location.city,
      });
    }
  }, [event]);

  // T018: PostHog event_opened. Keyed on event?.id to avoid double-fires
  // from React 18 strict mode and unrelated re-renders.
  useEffect(() => {
    if (!event) return;
    posthog.capture('event_opened', {
      event_id: event.id,
      category: event.category,
      city: event.location.city,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id]);

  if (loading) {
    return (
      <div className={styles.page}>
        <PageHeader />
        <main className={styles.main}>
          <div className={styles.loading}>
            <div className={styles.spinner} />
            <p className={styles.loadingText}>Caricamento evento...</p>
          </div>
        </main>
      </div>
    );
  }

  // T010 (R7): the event exists but its disputed date is under verification
  // (structured 409 DATE_UNDER_REVIEW). Distinct from the not-found block
  // below — this must never read as "deleted".
  if (dateUnderReview) {
    return (
      <div className={styles.page}>
        <PageHeader />
        <main ref={mainRef} className={styles.main}>
          <DateUnderReviewNotice className={styles.error} messageClassName={styles.errorTitle} />
        </main>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className={styles.page}>
        <PageHeader />
        <main ref={mainRef} className={styles.main}>
          <div className={styles.error}>
            <span className={styles.errorIcon}>😕</span>
            <h1 className={styles.errorTitle}>Evento non trovato</h1>
            <p className={styles.errorText}>
              {error || "L'evento richiesto non esiste o potrebbe essere stato rimosso."}
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader />

      <main className={styles.main}>
        <header className={styles.header}>
          <div className={styles.categoryChip}>
            <Chip variant="category" color={categoryColors[event.category]}>
              {categoryLabels[event.category] || event.category}
            </Chip>
            {event.isUnverified && <span className={styles.unverifiedBadge}>Non verificato</span>}
          </div>
          <h1 className={styles.title}>{event.title}</h1>

          <p className={styles.subtitle}>
            <span className={styles.subtitleItem}>
              <span className={styles.subtitleIcon}>📅</span>
              {formatDateRange(event.startDate, event.endDate)}
              {event.startTime && (
                <span className={styles.subtitleTime}>
                  {' · '}
                  {event.startTime.slice(0, 5)}
                  {event.endTime ? `-${event.endTime.slice(0, 5)}` : ''}
                </span>
              )}
            </span>
            <span className={styles.subtitleItem}>
              <BuzzPin size={14} className={styles.subtitleIcon} />
              {event.location.city}
              {event.location.province && ` (${event.location.province})`}
            </span>
          </p>
        </header>

        {(() => {
          const sponsorImgs =
            event.sponsorImages && event.sponsorImages.length > 0 ? event.sponsorImages : [];
          const heroImg =
            sponsorImgs.length === 0 && hasRealImage(event) && event.imageUrl
              ? [event.imageUrl]
              : [];
          const images = [...sponsorImgs, ...heroImg];
          // key sull'evento: cambiando evento si riparte da foto non fallite e dalla prima slide.
          return images.length > 0 ? <ImageGallery key={event.id} images={images} /> : null;
        })()}

        <EventMeta event={event} />

        {/* TODO: Sponsor e Bancarelle - riabilitare quando i dati arrivano dal DB
        {mockSponsor && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Sponsor</h2>
            <SponsorBanner sponsor={mockSponsor} />
          </section>
        )}

        {mockStands.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Bancarelle</h2>
            <StandList stands={mockStands} />
          </section>
        )}
        */}
      </main>

      <EventActions event={event} />
    </div>
  );
}
